import {
  ButtonsBloc,
  FGEmpty,
  FGTextInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import * as Yup from "yup";

import { ErrorDetails, ESRSHExceptions, WebUserApi } from "../../../../api";
import { SaveButton } from "../../../../components";
import { useAuth } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalFormGenerator } from "../../../common/DrupalFormGenerator";

export interface IDrupalProfileChangePasswordTabProps {}

type PasswordData = {
  oldPassword: string;
  password1: string;
  password2: string;
};

export const DrupalProfileChangePasswordTab: React.FunctionComponent<IDrupalProfileChangePasswordTabProps> = () => {
  const { t } = useTl();
  const api = useApiService(WebUserApi);
  const { user } = useAuth();

  const initialData = React.useMemo(
    () => ({
      oldPassword: "",
      password1: "",
      password2: "",
    }),
    []
  );

  const [isSaving, setIsSaving] = React.useState(false);
  const save = React.useCallback(
    async (data: PasswordData) => {
      try {
        setIsSaving(true);
        await api.webUserChangePassword(data.oldPassword, data.password1);
        showSuccess(t(ETLCodes.PasswordChanged));
      } catch (error) {
        const details: ErrorDetails = JSON.parse(error.response);
        if (details?.errorCode === ESRSHExceptions.OldPasswordIncorrect) {
          showError(t(ETLCodes.IncorrectPassword));
        } else {
          showError(t(ETLCodes.ErrorWhileSavingPassword));
        }
      }
      setIsSaving(false);
    },
    [api, t]
  );

  const validationSchema = React.useMemo(() => {
    return Yup.object().shape({
      oldPassword: Yup.string().nullable().required(t(ETLCodes.Required)),
      password1: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("len", t(ETLCodes.PasswordComplexityMessage), (val) => (val?.length || 0) >= 8), //TODO : add Translations...
      password2: Yup.string()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("is-equal", t(ETLCodes.PasswordMustMatch), async function (value) {
          return value === this.parent.password1;
        }),
    });
  }, [t]);
  return (
    <>
      <DrupalFormGenerator
        initialValues={initialData}
        onSubmit={save}
        validationSchema={validationSchema}
        saving={isSaving}
        hideButtons={true}
        minLabelWidth={280}
      >
        <FieldGroup columns={2}>
          <FieldGroup columns={1}>
            <FGTextInput type="password" name="oldPassword" label={t(ETLCodes.OldPassword)} />
            <FGTextInput type="password" name="password1" label={t(ETLCodes.NewPassword)} />
            <FGTextInput type="password" name="password2" label={t(ETLCodes.NewPasswordConfirmation)} />
          </FieldGroup>
          <FGEmpty />
        </FieldGroup>
        <InlineButtonContainer>
          <div></div>
          <ButtonsBloc>
            <SaveButton minimal={false} loading={isSaving} text={t(ETLCodes.General_Save)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      </DrupalFormGenerator>
    </>
  );
};
